import React, { useMemo } from 'react';
import { MovementCategory, PaymentMethod, UserRole, UserType } from '../types/index';
import { format } from 'date-fns';
import { formatPtLongDate } from '../utils/dateI18n';
import { Plus, TrendingUp, CreditCard, DollarSign, Clock, ArrowRight, ReceiptText, ChevronRight, Split } from 'lucide-react';
import { traduzirMetodoPagamento, pagamentosDoMovimento } from '../shared/logica/pagamentos';

export default function DashboardView({ company, movements, onNewMovement, onViewAll, currentUser }) {
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  
  // Filtrar movimentos que têm pagamentos hoje (considerando pagamentos mistos)
  const todayMovements = useMemo(() => {
    return movements.filter(m => {
      if (m.isMixedPayment) {
        return pagamentosDoMovimento(m).some(p => {
          const paymentDate = (p.paymentDate || m.date || '').split('T')[0];
          return paymentDate === todayStr;
        });
      }
      return m.date === todayStr;
    });
  }, [movements, todayStr]);

  const stats = useMemo(() => {
    let totalSold = 0;
    let cash = 0;
    let card = 0;
    let pending = 0;

    movements.forEach(m => {
      if (m.category !== MovementCategory.OPERATIONAL) return;

      if (m.isMixedPayment) {
        pagamentosDoMovimento(m).forEach(p => {
          const paymentDate = (p.paymentDate || m.date || '').split('T')[0];
          if (paymentDate !== todayStr) return;
          totalSold += p.value;
          if (p.method === PaymentMethod.CASH) cash += p.value;
          if (p.method === PaymentMethod.CARD) card += p.value;
          if (p.method === PaymentMethod.PENDING) pending += p.value;
        });
      } else {
        // Para pagamentos simples, verificar a data principal
        if (m.date === todayStr) {
          const val = Number(m.value || 0);
          totalSold += val;
          if (m.paymentMethod === PaymentMethod.CASH) cash += val;
          if (m.paymentMethod === PaymentMethod.CARD) card += val;
          if (m.paymentMethod === PaymentMethod.PENDING) pending += val;
        }
      }
    });

    return { 
        totalSold, 
        byMethod: {
            [PaymentMethod.CASH]: cash,
            [PaymentMethod.CARD]: card,
            [PaymentMethod.PENDING]: pending,
        }
    };
  }, [movements, todayStr]);

  const isComplexEmployee = currentUser?.role === UserRole.EMPLOYEE && currentUser?.userType === UserType.COMPLEX;

  return (
    <div className="max-w-[1400px] mx-auto animate-in fade-in duration-500 space-y-8">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{company.name}</h1>
          <div className="flex items-center gap-2 text-slate-500 mt-2 font-medium">
            <Clock size={16} />
            <p className="capitalize">{formatPtLongDate(new Date())}</p>
          </div>
        </div>
        <button 
          onClick={onNewMovement}
          className="flex items-center gap-2 bg-[#10b981] text-white px-6 py-3 rounded-xl font-bold hover:bg-[#059669] transition-all transform active:scale-95"
        >
          <Plus size={20} /> Novo Movimento
        </button>
      </header>

      {!isComplexEmployee && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-[#10b981] p-6 rounded-[2rem] shadow-xl shadow-emerald-50 text-white relative overflow-hidden">
            <TrendingUp size={28} className="opacity-80" />
            <div className="mt-8">
              <p className="text-3xl font-bold leading-tight break-words">{stats.totalSold.toFixed(2)}€</p>
              <p className="text-sm font-medium opacity-80 mt-1">Total do dia</p>
            </div>
          </div>
          <SummaryCard title="Dinheiro" value={stats.byMethod[PaymentMethod.CASH]} icon={<DollarSign className="text-emerald-500" />} />
          <SummaryCard title="Multibanco" value={stats.byMethod[PaymentMethod.CARD]} icon={<CreditCard className="text-blue-500" />} />
          <SummaryCard title="Pendente" value={stats.byMethod[PaymentMethod.PENDING]} icon={<Clock className="text-amber-500" />} />
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden min-h-[400px]">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-slate-800">Movimentos de hoje</h2>
            <span className="text-slate-400 text-sm">({todayMovements.length} movimentos)</span>
          </div>
          <button onClick={onViewAll} className="text-[#1b5e20] font-bold text-sm flex items-center gap-1 hover:underline">
            Ver todos <ArrowRight size={16} />
          </button>
        </div>

        <div className="p-8">
          {todayMovements.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="text-slate-400 text-xs font-bold uppercase tracking-wider">
                  <tr>
                    <th className="pb-4">Tipo</th>
                    <th className="pb-4">Cliente</th>
                    <th className="pb-4">Método</th>
                    <th className="pb-4 text-right">Valor</th>
                    <th className="pb-4 text-center"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {todayMovements.map(m => (
                    <tr key={m.id} className="group hover:bg-slate-50/50 transition-colors cursor-pointer" onClick={onViewAll}>
                      <td className="py-4 font-bold text-slate-900 capitalize">{m.type.toLowerCase().replace('_', ' ')}</td>
                      <td className="py-4 text-slate-600 font-medium">{m.client || 'Consumidor Final'}</td>
                      <td className="py-4">
                        {m.isMixedPayment ? (
                            <div className="flex flex-col gap-0.5">
                                {pagamentosDoMovimento(m).map((p, idx) => (
                                  <span key={idx} className="flex items-center gap-1 text-[9px] font-black text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded w-fit uppercase">
                                    <Split size={8} /> {traduzirMetodoPagamento(p.method)}
                                  </span>
                                ))}
                            </div>
                        ) : (
                            <span className={`text-[10px] font-bold px-2 py-1 rounded-md ${
                                m.paymentMethod === PaymentMethod.CASH ? 'bg-emerald-50 text-emerald-600' :
                                m.paymentMethod === PaymentMethod.CARD ? 'bg-blue-50 text-blue-600' :
                                m.paymentMethod === PaymentMethod.VOUCHER ? 'bg-purple-50 text-purple-600' :
                                m.paymentMethod === PaymentMethod.TRANSFER ? 'bg-indigo-50 text-indigo-600' :
                                m.paymentMethod === PaymentMethod.CHEQUE ? 'bg-orange-50 text-orange-600' :
                                m.paymentMethod === PaymentMethod.PENDING ? 'bg-red-50 text-red-600' :
                                m.paymentMethod === PaymentMethod.MBWAY ? 'bg-cyan-50 text-cyan-600' :
                                'bg-amber-50 text-amber-600'
                            }`}>
                                {traduzirMetodoPagamento(m.paymentMethod)}
                            </span>
                        )}
                      </td>
                      <td className="py-4 text-right font-black text-slate-900">{m.value.toFixed(2)}€</td>
                      <td className="py-4 text-center text-slate-300">
                        <ChevronRight size={18} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-8 flex justify-center">
                <button 
                  onClick={onViewAll}
                  className="px-6 py-3 border-2 border-[#10b981] text-[#10b981] font-bold rounded-2xl hover:bg-[#10b981] hover:text-white transition-all flex items-center gap-2"
                >
                  Ver histórico completo <ArrowRight size={18} />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
                <ReceiptText size={40} className="text-slate-300" />
              </div>
              <p className="text-lg font-bold text-slate-800 mb-2">Sem movimentos hoje</p>
              <p className="text-sm font-medium text-slate-500 mb-8">Comece por adicionar um novo movimento</p>
              <button 
                onClick={onNewMovement}
                className="bg-[#10b981] text-white px-8 py-3 rounded-xl font-bold flex items-center gap-2 hover:bg-[#059669] transition-colors"
              >
                <Plus size={20} /> Adicionar movimento
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ title, value, icon }) {
  return (
    <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col justify-between">
      <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center">
        {icon}
      </div>
      <div className="mt-8">
        <p className="text-2xl font-black text-slate-900 leading-tight break-words">{Number(value || 0).toFixed(2)}€</p>
        <p className="text-sm font-medium text-slate-400 mt-1">{title}</p>
      </div>
    </div>
  );
}
